import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Loader2 } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";

interface EventDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: {
    id: number;
    title: string;
    date: Date;
    time: string;
    description: string;
    eventType?: string;
    serviceLocation?: string;
    _id?: string; // Add the database ID for edit/delete operations
    assignedStaff?: string[];
    businessContext?: any;
    location?: string;
    address?: string;
  } | null;
  serviceLocations?: Array<{ id: string; name: string }>;
  onEditEvent?: (event: any) => void;
}

const EventDetailsSheet: React.FC<EventDetailsSheetProps> = ({
  open,
  onOpenChange,
  event,
  serviceLocations = [],
  onEditEvent,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete event mutation
  const deleteEvent = useMutation(api.calendar.deleteEvent);

  if (!event) return null;

  const getLocationName = (locationId?: string) => {
    if (!locationId) return "Not specified";
    const location = serviceLocations.find((loc) => loc.id === locationId);
    return location ? location.name : "Unknown location";
  };

  const handleDeleteEvent = async () => {
    if (!event._id) return;

    setIsDeleting(true);
    try {
      await deleteEvent({ id: event._id as any });
      setDeleteDialogOpen(false);
      onOpenChange(false); // Close the details sheet
    } catch (error) {
      console.error("Error deleting event:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditEvent = () => {
    if (onEditEvent && event) {
      onEditEvent(event);
      onOpenChange(false); // Close the details sheet
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>{event.title}</SheetTitle>
          <SheetDescription>View details for this calendar event.</SheetDescription>
        </SheetHeader>
        <div className="grid max-h-[calc(100vh-180px)] gap-4 overflow-y-auto py-4">
          <div className="grid grid-cols-[100px_1fr] items-center gap-2">
            <span className="font-medium">Date:</span>
            <span>{event.date.toLocaleDateString()}</span>
          </div>
          <div className="grid grid-cols-[100px_1fr] items-center gap-2">
            <span className="font-medium">Time:</span>
            <span>{event.time}</span>
          </div>
          {event.eventType && (
            <div className="grid grid-cols-[100px_1fr] items-center gap-2">
              <span className="font-medium">Type:</span>
              <span className="capitalize">{event.eventType}</span>
            </div>
          )}
          {(event.location || event.address) && (
            <div className="grid grid-cols-[100px_1fr] items-center gap-2">
              <span className="font-medium">Location:</span>
              <span>
                {event.location && event.address
                  ? `${event.location}, ${event.address}`
                  : event.location || event.address || getLocationName(event.serviceLocation)}
              </span>
            </div>
          )}
          {event.assignedStaff && event.assignedStaff.length > 0 && (
            <div className="grid grid-cols-[100px_1fr] items-center gap-2">
              <span className="font-medium">Assigned:</span>
              <span>{event.assignedStaff.length} staff member(s)</span>
            </div>
          )}
          {event.businessContext && (
            <div className="grid grid-cols-[100px_1fr] items-center gap-2">
              <span className="font-medium">Business:</span>
              <span>{event.businessContext.name}</span>
            </div>
          )}
          <div className="grid grid-cols-[100px_1fr] items-start gap-2">
            <span className="font-medium">Description:</span>
            <span>{event.description || "No description"}</span>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          {event._id && onEditEvent && (
            <div className="flex gap-2">
              <Button onClick={handleEditEvent} className="flex-1 gap-2">
                <Edit className="h-4 w-4" />
                Edit Event
              </Button>
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                variant="destructive"
                className="flex-1 gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
          <Button onClick={() => onOpenChange(false)} variant="outline" className="w-full">
            Close
          </Button>
        </div>
      </SheetContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete "{event.title}" and remove
              it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
};

export default EventDetailsSheet;
